import { useEffect, useRef, useState } from 'react'
import { env } from '~/env.ts'
import { v4 as uuidv4 } from 'uuid'
import WebViewer, { WebViewerInstance } from '@pdftron/webviewer'
import { useParams, useNavigate } from 'react-router'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  useFetchDocList,
  useFetchWorkflow,
  useUpdateStepTaskResult
} from '~/hooks/submittals'
import {
  getWorkflowStepResult,
  updateWorkflowStepResult,
  WorkflowStepBoundingBoxResult,
  WorkflowStepResult,
  WorkflowStepTableResult
} from '~/lib/mbue-api/services/workflowStepResult'
import { toast } from 'sonner'
import { zoomToArea } from '~/utilities/webviewer/zoomToArea'

import EditableTable from '~/components/Submittals/EditableTable'
import Button from '~/components/ui/Button'
import Loading from '~/components/ui/Loading'
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle
} from '~/components/ui/Resizable'
import { WorkflowStepStatus } from 'mbue-rest-client'

import { useFlag } from '~/hooks/useFlag'
import { getFirefoxVersion } from '~/utilities/utils'

const mapWorkflowStepResultToTableData = (workflowStepResult: WorkflowStepResult) => {
  const workflowStepTableResult = workflowStepResult as WorkflowStepTableResult
  const tableData = workflowStepTableResult.table.rows.map((row) => {
    return row.values.reduce(
      (accum, value, index) => {
        if (!workflowStepTableResult.table.columns[index]) {
          return accum
        }

        if (index === 0) {
          return {
            ...accum,
            ID: uuidv4(),
            [workflowStepTableResult.table.columns[index]]: value
          }
        }

        return {
          ...accum,
          [workflowStepTableResult.table.columns[index]]: value
        }
      },
      {} as Record<string, string>
    )
  })

  return tableData
}

export default function ConfirmSpecifiedProducts() {
  const navigate = useNavigate()
  const viewerDivRef = useRef<HTMLDivElement>(null)
  const isViewerInitialized = useRef<boolean>(false)
  const [isDocumentLoading, setIsDocumentLoading] = useState<boolean>(true)
  const [tableData, setTableData] = useState<Record<string, string>[]>([])

  const isProdViewerEnabled = useFlag('apryse-prod-viewer-enabled')

  const { projectId, workflowInstanceId } = useParams<{
    projectId: string
    workflowInstanceId: string
  }>()

  const { data: workflowWithData, isLoading: workflowWithDataIsLoading } =
    useFetchWorkflow({
      projectId: parseInt(projectId!, 10),
      workflowInstanceId: workflowInstanceId!
    })

  const { data: docList, isLoading: isLoadingDocList } = useFetchDocList(
    { projectId: parseInt(projectId!, 10) },
    {
      refetchInterval: false,
      retry: false
    }
  )

  const { data: workflowStepBoxResult, isLoading: isWorkflowStepBoxResultLoading } =
    useQuery({
      queryKey: ['workflowStepBoxResult', projectId, workflowInstanceId],
      queryFn: () =>
        getWorkflowStepResult({
          projectId: Number(projectId),
          workflowInstanceId: workflowInstanceId!,
          stepIndex: 1
        }),
      enabled: !!projectId && !!workflowInstanceId,
      retry: false
    })

  const { data: workflowStepResult, isLoading: isWorkflowStepResultLoading } = useQuery({
    queryKey: ['workflowStepTableResult', projectId, workflowInstanceId],
    queryFn: () =>
      getWorkflowStepResult({
        projectId: Number(projectId),
        workflowInstanceId: workflowInstanceId!,
        stepIndex: 2
      }),
    enabled: !!projectId && !!workflowInstanceId,
    retry: false
  })

  const { mutateAsync: updateStepTaskResult, isPending: isPendingUpdateStepTaskResult } =
    useUpdateStepTaskResult(parseInt(projectId!, 10), workflowInstanceId!, 2, 0, {
      onSuccess: () => {
        navigate(location.pathname.split('/').slice(0, -1).join('/'))
      }
    })

  const updateWorkflowStepResultMutation = useMutation({
    mutationFn: updateWorkflowStepResult,
    onSuccess: async () => {
      await updateStepTaskResult({
        status: true
      })
      toast.success('Product Table updated successfully')
    },
    onError: (error) => {
      toast.error('Error updating product table')
      console.error('Error updating workflow step result:', error)
    }
  })

  useEffect(() => {
    if (workflowStepResult) {
      const [scheduleTableResult] =
        workflowStepResult as unknown as WorkflowStepBoundingBoxResult[]
      setTableData(mapWorkflowStepResultToTableData(scheduleTableResult))
    }
  }, [workflowStepResult, navigate])

  useEffect(() => {
    if (isProdViewerEnabled === null) return
    let webviewerKey: string = env.VITE_WEBVIEWER_DEMO_TOKEN ?? ''

    if (isProdViewerEnabled && env.VITE_WEBVIEWER_TOKEN) {
      webviewerKey = env.VITE_WEBVIEWER_TOKEN
    }

    if (!docList || isWorkflowStepBoxResultLoading || !workflowStepBoxResult) return

    const documentUri = docList?.drawing_doc.download_url?.[0] ?? ''

    let webviewerInstance: WebViewerInstance

    const initWebviewer = async () => {
      if (isViewerInitialized.current) return

      isViewerInitialized.current = true

      try {
        webviewerInstance = await WebViewer.Iframe(
          {
            path: '/webviewer-0ad25b9a',
            licenseKey: webviewerKey,
            preloadWorker: 'pdf',
            fullAPI: true,
            ui: 'legacy'
          },
          viewerDivRef.current!
        )

        const { Core, UI } = webviewerInstance
        const { iframeWindow } = UI
        const { documentViewer } = Core

        const version = getFirefoxVersion()

        if (version && version > 145) {
          console.log('Disabling virtual display mode for Firefox version', version)
          const displayMode = Core.documentViewer.getDisplayModeManager()
          displayMode.disableVirtualDisplayMode()
        }

        documentViewer.addEventListener('documentLoaded', () => {
          const [boxResult] =
            workflowStepBoxResult as unknown as WorkflowStepBoundingBoxResult[]
          const schedulePage = boxResult?.document_bounding_box?.page_ix ?? 1
          const boundingBox = boxResult?.document_bounding_box.bounding_box

          documentViewer.addEventListener(
            'pageComplete',
            async () => {
              if (!boundingBox) return

              zoomToArea(
                webviewerInstance,
                schedulePage,
                boundingBox.xmin,
                boundingBox.ymin,
                boundingBox.xmax,
                boundingBox.ymax
              )

              await new Promise((resolve) => setTimeout(resolve, 500))

              iframeWindow.document
                .querySelector<HTMLButtonElement>('[data-element="zoomOutButton"]')
                ?.click()
            },
            { once: true }
          )

          documentViewer.setCurrentPage(schedulePage, false)

          setIsDocumentLoading(false)
        })

        void documentViewer.loadDocument(documentUri, { extension: 'pdf' })
      } catch (error) {
        console.error('Error initializing WebViewer:', error)
      }
    }

    void initWebviewer()
  }, [
    docList,
    isWorkflowStepBoxResultLoading,
    workflowStepBoxResult,
    isProdViewerEnabled
  ])

  const hasShownCompletionToastRef = useRef(false)

  useEffect(() => {
    if (workflowWithDataIsLoading) return

    if (
      !workflowWithDataIsLoading &&
      workflowWithData?.steps?.[2]?.state?.status === WorkflowStepStatus.COMPLETED &&
      !hasShownCompletionToastRef.current
    ) {
      toast.success('Step Completed', {
        description: 'This is a read only version of the schedule'
      })
      hasShownCompletionToastRef.current = true
    }
  }, [workflowWithData, workflowWithDataIsLoading])

  const onConfirmAndContinue = () => {
    if (!workflowStepResult) return
    const [scheduleTableResult] =
      workflowStepResult as unknown as WorkflowStepTableResult[]
    const stepData = structuredClone(
      scheduleTableResult
    ) as unknown as WorkflowStepTableResult
    stepData.table.columns = stepData.table.columns.filter((column) => column !== '')
    tableData.forEach((row, index) => {
      stepData.table.rows[index] = {
        ...(stepData.table.rows[index] ?? {
          cell_coordinates: [],
          confidence_scores: [100],
          approved: false
        }),
        values: Object.values(row).slice(1)
      }
    })

    updateWorkflowStepResultMutation.mutate({
      projectId: Number(projectId),
      workflowInstanceId: workflowInstanceId!,
      stepIndex: 2,
      stepData: {
        result: stepData
      }
    })
  }
  const isStepCompleted =
    workflowWithData?.steps?.[2]?.state?.status === WorkflowStepStatus.COMPLETED

  const scheduleTableResult = Array.isArray(workflowStepResult)
    ? (workflowStepResult[0] as unknown as WorkflowStepTableResult)
    : (workflowStepResult as unknown as WorkflowStepTableResult)
  return (
    <>
      {(isLoadingDocList || isWorkflowStepResultLoading || isDocumentLoading) && (
        <Loading mode="dark" position="fixed" />
      )}
      <div className="w-full h-full flex flex-col">
        <div className="h-[calc(100vh-56px)] w-full">
          <ResizablePanelGroup direction="horizontal">
            <ResizablePanel defaultSize={50} minSize={30}>
              <div className="h-full viewer" ref={viewerDivRef} />
            </ResizablePanel>
            <ResizableHandle withHandle className="border border-content" />
            <ResizablePanel defaultSize={50} minSize={30}>
              <div className="w-full bg-[#333] flex flex-col">
                <div className="h-[52px] w-full bg-[#333]"></div>
                <div className="h-[calc(100vh-164px)] overflow-auto">
                  <EditableTable
                    data={tableData}
                    setData={setTableData}
                    columns={
                      scheduleTableResult?.table?.columns?.filter(
                        (column) => column !== ''
                      ) ?? []
                    }
                  />
                </div>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>

        <div className="w-full flex justify-between px-4 py-2 items-center ">
          <div>
            Review the table and confirm that all data was imported correctly. Edit as
            needed to fix any errors or missing info.
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Exit
            </Button>
            <Button
              onClick={onConfirmAndContinue}
              disabled={
                updateWorkflowStepResultMutation.isPending ||
                isPendingUpdateStepTaskResult ||
                isStepCompleted
              }
            >
              {updateWorkflowStepResultMutation.isPending || isPendingUpdateStepTaskResult
                ? 'Updating...'
                : 'Confirm & Continue'}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
